import React, { useState } from "react";
import { Path, useFieldArray, useForm } from "react-hook-form";
import { ApplicatorDto } from "../../requests/useGetApplicators";
import useUpsertApplicator, { ApplicatorUpsertDto } from "../../requests/useUpsertApplicator";
import { FormTextControl, RowFormText } from "../Common/Forms/FormText";
import KaAlert from "../Common/KaAlert";
import KaModal from "../Common/KaModal";
import SpinnerButton from "../Common/SpinnerButton";
import useDisplayMessage from "../Common/useDisplayMessage";
import { FormLabel } from "../Common/Forms/FormLabel";
import { Col, FormGroup } from "reactstrap";
import BoldButton from "../Common/BoldButton";

type ApplicatorForm = Omit<ApplicatorUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

interface CreateEditApplicatorProps {
  initialApplicator: ApplicatorDto | null;
  onDismiss: () => void;
  onUpsert: (applicatorId: string, applicatorName: string, assignSites: boolean) => void;
}

export const CreateEditApplicator = (props: CreateEditApplicatorProps) => {
  const displayMessage = useDisplayMessage();
  const { id, ...initialApplicator } = { ...props.initialApplicator };
  const initEmails = initialApplicator.emailAddresses?.map(email => ({ email })) ?? [];
  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<ApplicatorForm>({
    defaultValues: {
      ...initialApplicator,
      emailAddresses: initEmails.length === 0 ? [{ email: '' }] : initEmails,
    }
  });

  const emailAddresses = useFieldArray({ control, name: 'emailAddresses' });

  const validateUniqueEmail = (value: string) => {
    if (value && getValues('emailAddresses').map(f => f.email).filter(e => e === value).length > 1) {
      return 'Email already exists';
    }
    return true;
  };

  const removeEmail = (index: number) => {
    emailAddresses.remove(index);
    trigger('emailAddresses');
  }

  const [assignSites, setAssignSites] = useState(false);

  const dismissed = () => {
    props.onDismiss();
  };

  const submit = (formValue: ApplicatorForm, assign: boolean) => {
    setAssignSites(assign);
    upsert.request({
      ...formValue,
      emailAddresses: formValue.emailAddresses.map(e => e.email).filter(e => !!e),
    });
  }

  const hookProps = (name: Path<ApplicatorForm>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name: name,
    setValue,
    register,
    errors,
  });

  const upsert = useUpsertApplicator(props.initialApplicator?.id, {
    onSuccess: response => {
      props.onDismiss();
      const applicatorId = props.initialApplicator ? props.initialApplicator.id : response;
      props.onUpsert(applicatorId, getValues().name, assignSites);
    },
    onError: (e) => displayMessage.fail(e.message),
  });

  const labelSpace = 4;
  const inputSpace = 7;

  const emailControl = (index: number) =>
    <FormTextControl
      {...hookProps(`emailAddresses.${index}.email`)}
      displayName='Email address'
      options={{
        onChange: () => trigger('emailAddresses'),
        validate: validateUniqueEmail,
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Email Address is invalid'
        }
      }} />

  return <KaModal
    title={`${props.initialApplicator ? 'Update' : 'Create'} a applicator`} onHide={dismissed} show={true}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText {...hookProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps('license')}
        displayName="License"
        options={{
          validate: { length: v => v.length < 51 || 'License cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps("epaNumber")}
        displayName="EPA Number"
        options={{
          validate: {
            length: v => v.length < 51 || 'EPA Number cannot be longer than 50 characters'
          }
        }} />
      {
        emailAddresses.fields.map((field, index) => {
          const isFirst = index === 0;
          const showX = emailAddresses.fields.length > 1;
          return <FormGroup row key={field.id}>
            {isFirst &&
              <Col xs={{ size: labelSpace }}>
                <FormLabel displayName='Email' {...hookProps(`emailAddresses.${index}.email`)} />
              </Col>
            }
            <Col xs={{ offset: (isFirst ? 0 : labelSpace), size: inputSpace - (showX ? 1 : 0) }}>
              {emailControl(index)}
            </Col>
            {
              showX &&
              <Col xs="1" className="pe-0">
                <BoldButton className='btn-close btn-sm mt-2' onClick={() => removeEmail(index)} />
              </Col>
            }
          </FormGroup>
        })}
      <FormGroup row>
        <Col xs={{ offset: labelSpace, size: inputSpace }} className='text-end'>
          <BoldButton
            className='col-auto btn-ghost-primary'
            disabled={emailAddresses.fields.length >= 10}
            onClick={() => emailAddresses.append({ email: '' })}>
            Add Email
          </BoldButton>
        </Col>
      </FormGroup>
    </>)}
    footer={(
      <div className="btn-group">
        <SpinnerButton
          className="w-25 m-1 rounded btn-ghost-primary"
          spinning={upsert.isLoading && !upsert.isError}
          onClick={handleSubmit(values => submit(values, false))}>
          {`${props.initialApplicator ? 'Update' : 'Create'}`}
        </SpinnerButton>
        <SpinnerButton
          className="w-25 m-1 rounded btn-ghost-primary"
          spinning={upsert.isLoading && !upsert.isError}
          onClick={handleSubmit(values => submit(values, true))}>
          {`${props.initialApplicator ? 'Update and Assign' : 'Create and Assign'}`}
        </SpinnerButton>
      </div>)
    }
  />
};
